import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faArrowRight } from "@fortawesome/pro-solid-svg-icons";

import { ButtonNew as Button } from "components/buttonNew";

import { getVisiblePages } from "./getVisiblePages";

import "./numberedPagination.scss";

interface INumberedPaginationProps {
  /** 1-based */
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  disabled?: boolean;
  className?: string;
}

export { getVisiblePages };

function PageJump(props: { totalPages: number; disabled: boolean; onPageChange: (page: number) => void }) {
  const { totalPages, disabled, onPageChange } = props;
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const skipSubmitRef = useRef(false);
  const submittedRef = useRef(false);

  useEffect(() => {
    if (open) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [open]);

  function close() {
    skipSubmitRef.current = true;
    setOpen(false);
    setValue("");
  }

  function submit(nextValue: string) {
    if (skipSubmitRef.current || submittedRef.current) {
      skipSubmitRef.current = false;
      return;
    }

    submittedRef.current = true;

    const page = Number(nextValue);

    if (Number.isInteger(page) && page >= 1 && page <= totalPages) {
      onPageChange(page);
    }

    setOpen(false);
    setValue("");
  }

  if (open) {
    return (
      <input
        ref={inputRef}
        className="ui-numbered-pagination-page ui-numbered-pagination-page-jump"
        aria-label={t("Go to page")}
        inputMode="numeric"
        maxLength={String(totalPages).length}
        value={value}
        disabled={disabled}
        onChange={event => setValue(event.target.value.replace(/\D/g, ""))}
        onBlur={event => submit(event.target.value)}
        onKeyDown={event => {
          if (event.key === "Enter") {
            event.preventDefault();
            submit(value);
          }

          if (event.key === "Escape") {
            event.preventDefault();
            close();
          }
        }}
      />
    );
  }

  return (
    <button
      type="button"
      className="ui-numbered-pagination-page"
      aria-label={t("Go to page")}
      disabled={disabled}
      onClick={() => {
        submittedRef.current = false;
        setOpen(true);
      }}
    >
      ...
    </button>
  );
}

export default function NumberedPagination(props: INumberedPaginationProps) {
  const { currentPage, totalPages, onPageChange, disabled = false, className } = props;
  const { t } = useTranslation();
  const visiblePages = getVisiblePages(currentPage, totalPages);

  return (
    <nav className={classNames("ui-numbered-pagination", className)} aria-label={t("Pagination")}>
      <Button
        type="secondary"
        size="small"
        disabled={disabled || currentPage <= 1}
        onClick={() => onPageChange(currentPage - 1)}
      >
        <FontAwesomeIcon icon={faArrowLeft} />
        {t("Previous")}
      </Button>

      <div className="ui-numbered-pagination-pages">
        {visiblePages.map((page, index) =>
          page === "ellipsis" ? (
            <PageJump
              key={`ellipsis-${index}`}
              totalPages={totalPages}
              disabled={disabled}
              onPageChange={onPageChange}
            />
          ) : (
            <button
              key={page}
              type="button"
              className={classNames("ui-numbered-pagination-page", {
                "ui-numbered-pagination-page-active": page === currentPage,
              })}
              aria-current={page === currentPage ? "page" : undefined}
              disabled={disabled || page === currentPage}
              onClick={() => onPageChange(page)}
            >
              {page}
            </button>
          ),
        )}
      </div>

      <Button
        type="secondary"
        size="small"
        disabled={disabled || currentPage >= totalPages}
        onClick={() => onPageChange(currentPage + 1)}
      >
        {t("Next")}
        <FontAwesomeIcon icon={faArrowRight} />
      </Button>
    </nav>
  );
}
